"use client";

import { Loader2 } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { Input } from "@/components/ui/input";
import { CalculatorNumberField } from "@/components/calculators/shared/calculator-number-field";
import { isOneOf } from "@shared/validation/is-one-of";
import { LTL_MAX_PALLETS, type PalletShippingMode } from "@shared/freight/shipping-mode";
import {
  EQUIPMENT_TYPES,
  PALLET_RANGE,
  WEIGHT_RANGE,
  type EquipmentType,
  type FreightQuoteInputs,
} from "./freight-quote-request";

const EQUIPMENT_OPTIONS = [
  { value: "DRY", label: "Dry Van" },
  { value: "REFRIGERATED", label: "Refrigerated" },
  { value: "FLATBED", label: "Flatbed" },
] satisfies ReadonlyArray<{ value: EquipmentType; label: string }>;

const PALLET_MODE_DESCRIPTIONS: Record<PalletShippingMode, string> = {
  LTL: `Ships LTL at ${LTL_MAX_PALLETS} pallets or fewer.`,
  FTL: "Ships as a full truckload.",
};

const WEIGHT_DESCRIPTIONS: Record<PalletShippingMode, string> = {
  LTL: "Required for LTL.",
  FTL: "Optional for a full truckload.",
};

interface ZipFieldProps {
  id: string;
  label: string;
  value: string;
  cityLabel?: string;
  placeholder: string;
  onChange: (value: string) => void;
}

function ZipField({ id, label, value, cityLabel, placeholder, onChange }: ZipFieldProps) {
  const descriptionId = cityLabel ? `${id}-city` : undefined;
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="postal-code"
        pattern="[0-9]*"
        maxLength={5}
        placeholder={placeholder}
        value={value}
        aria-describedby={descriptionId}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 5))}
      />
      {cityLabel ? <FieldDescription id={descriptionId}>{cityLabel}</FieldDescription> : null}
    </Field>
  );
}

export interface FreightFormProps {
  inputs: FreightQuoteInputs;
  shippingMode: PalletShippingMode | null;
  originLabel?: string;
  destLabel?: string;
  isLoading: boolean;
  canFetchQuote: boolean;
  onChange: <Key extends keyof FreightQuoteInputs>(key: Key, value: FreightQuoteInputs[Key]) => void;
  onFetchQuote: () => void;
}

function LoadFields({ inputs, shippingMode, onChange }: FreightFormProps) {
  const isLtl = shippingMode === "LTL";
  return (
    <div className="grid gap-5 sm:grid-cols-3">
      <CalculatorNumberField
        id="pallet-count"
        label="Pallet Count"
        value={inputs.pallets}
        range={PALLET_RANGE}
        description={shippingMode ? PALLET_MODE_DESCRIPTIONS[shippingMode] : undefined}
        onChange={(value) => onChange("pallets", value)}
      />
      <CalculatorNumberField
        id="total-weight"
        label="Total Weight (lb)"
        value={inputs.weight}
        range={WEIGHT_RANGE}
        allowBlank
        required={isLtl}
        description={shippingMode ? WEIGHT_DESCRIPTIONS[shippingMode] : undefined}
        onChange={(value) => onChange("weight", value)}
      />
      <Field>
        <FieldLabel htmlFor="equipment-type">Equipment Type</FieldLabel>
        <InlineCombobox
          id="equipment-type"
          value={isLtl ? "DRY" : inputs.equipmentType}
          options={EQUIPMENT_OPTIONS}
          disabled={isLtl}
          onSelect={(value) => {
            if (isOneOf(EQUIPMENT_TYPES, value)) onChange("equipmentType", value);
          }}
          placeholder="Select…"
          searchPlaceholder="Search equipment"
        />
        {isLtl ? <FieldDescription>LTL ships on a dry van.</FieldDescription> : null}
      </Field>
    </div>
  );
}

export function FreightForm(props: FreightFormProps) {
  return (
    <div className="space-y-5 p-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <ZipField id="origin-zip" label="Origin ZIP Code" value={props.inputs.originZip} cityLabel={props.originLabel} placeholder="e.g. 60601" onChange={(value) => props.onChange("originZip", value)} />
        <ZipField id="dest-zip" label="Destination ZIP Code" value={props.inputs.destZip} cityLabel={props.destLabel} placeholder="e.g. 90210" onChange={(value) => props.onChange("destZip", value)} />
      </div>
      <LoadFields {...props} />
      <Button variant="primary" onClick={props.onFetchQuote} disabled={!props.canFetchQuote || props.isLoading} className="w-full">
        {props.isLoading ? (
          <>
            <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
            Getting Quote...
          </>
        ) : (
          "Get Freight Quote"
        )}
      </Button>
    </div>
  );
}
